import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import Header from '../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VolunteersPhonebook = () => {
    // --- State ---
    const [volunteers, setVolunteers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Hooks ---
    const navigate = useNavigate();

    // --- Context ---
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Volunteer's Phonebook",
            filterPlaceholder: "Search by name or mobile...",
            loadingData: "Loading Data...",
            noData: "No volunteers found.",
            call: "Call",
            members: "Members :",
            boothWiseBtn: "Booth Wise",
            totalSum: "Total Members :"
        },
        hi: {
            pageTitle: "स्वयंसेवक फोनबुक",
            filterPlaceholder: "नाम या मोबाइल द्वारा खोजें...",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई स्वयंसेवक नहीं मिला।",
            call: "कॉल",
            members: "सदस्य :",
            boothWiseBtn: "बूथ वार",
            totalSum: "कुल सदस्य :"
        },
        gu: {
            pageTitle: "સ્વયંસેવકની ફોનબુક",
            filterPlaceholder: "નામ અથવા મોબાઇલથી શોધો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ સ્વયંસેવક મળ્યા નથી.",
            call: "કૉલ",
            members: "સભ્યો :",
            boothWiseBtn: "બૂથ વાઈઝ",
            totalSum: "કુલ સભ્યો :"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchVolunteers = async () => {
        if (!appId || !userId) return;

        setLoading(true);
        setSearchTerm("");

        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_all_contact_match_admin`,
                {
                    app_id: appId,
                    user_id: userId
                }
            );

            if (response.data.success && Array.isArray(response.data.data.result)) {
                setVolunteers(response.data.data.result);
            } else {
                setVolunteers([]);
            }
        } catch (error) {
            console.error("Error fetching volunteers:", error);
            setVolunteers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVolunteers();
    }, [appId, userId]);

    // --- Filter Logic ---
    const filteredVolunteers = volunteers.filter((vol) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();

        const name = vol.name ? vol.name.toLowerCase() : "";
        const mobile = vol.mobile_no ? String(vol.mobile_no) : "";
        const desig = vol.designation ? vol.designation.toLowerCase() : "";

        return name.includes(term) || mobile.includes(term) || desig.includes(term);
    });

    // --- Calculate Total Members ---
    const totalMemberCount = filteredVolunteers.reduce((sum, vol) => {
        return sum + (parseInt(vol.total_phonebook_member) || 0);
    }, 0);

    // --- Navigation Handlers ---
    const handleBoothWiseClick = () => {
        navigate('/dis_booth_wise_phonebook');
    };

    const handleCardClick = (selectedUserId, selectedUserName) => {
        navigate('/dis_contact_group_member', {
            state: { userId: selectedUserId, userName: selectedUserName }
        });
    };

    return (
        <div className='app-container p-4'>

            {/* Reusable Header Component with empty subtitle */}
            <Header title={txt.pageTitle} subtitle="" />

            {/* Subtitle below header with Gradient Line (Option 7) */}
            {!loading && (
                <div className="subtitle-container mb-4">
                    <div className="subtitle-content">

                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            <div className="count-display-modern slide-up-element">
                                <span className="count-label">{lang === 'en' ? 'Contacts :' : 'संपर्क :'}</span>
                                <span className="count-number">{filteredVolunteers.length}</span>
                            </div>
                            <div className="count-display-modern slide-up-element">
                                <span className="count-label">{txt.totalSum}</span>
                                <span className="count-number">{totalMemberCount}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Search Filter & Button */}
            {volunteers.length > 0 && (
                <div className="filter-container mb-4">
                    <div className="search-row-flex">
                        <div className="search-wrapper">
                            <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                className="search-input"
                                type="text"
                                placeholder={txt.filterPlaceholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button className="btn-booth-wise" onClick={handleBoothWiseClick}>
                            {txt.boothWiseBtn}
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginLeft: '6px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Loading Spinner */}
            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {/* No Data State */}
            {!loading && filteredVolunteers.length === 0 && (
                <div className="text-center text-muted p-5 empty-state">
                    <div className="empty-icon">👥</div>
                    <p>{txt.noData}</p>
                </div>
            )}

            {/* Volunteer List Grid */}
            <div className="row g-4 pb-5">
                {!loading && filteredVolunteers.map((vol, index) => {
                    const uniqueKey = vol.user_id ? `vol-${vol.user_id}` : `vol-${index}`;
                    const userTypeLabel = vol.designation || (vol.user_type === 'A' ? 'Admin' : (vol.user_type === 'k' ? 'Karyakarta' : 'User'));

                    let badgeClass = 'badge-blue';
                    if (vol.user_type === 'A' || userTypeLabel.toLowerCase().includes('admin')) badgeClass = 'badge-red';
                    else if (vol.user_type === 'k' || userTypeLabel.toLowerCase().includes('karya')) badgeClass = 'badge-orange';

                    let avatarBg = '#f3f4f6'; let avatarText = '#6b7280';
                    if (badgeClass === 'badge-red') { avatarBg = '#fee2e2'; avatarText = '#dc2626'; }
                    else if (badgeClass === 'badge-orange') { avatarBg = '#fef3c7'; avatarText = '#d97706'; }
                    else { avatarBg = '#e0f2fe'; avatarText = '#0284c7'; }

                    const memberCount = vol.total_phonebook_member || 0;

                    return (
                        <div className="col-12 col-lg-3" key={uniqueKey}>
                            {/* FIX 1: Added h-100 to make all cards equal height */}
                            <div className="volunteer-card-web clickable-card h-100" onClick={() => handleCardClick(vol.user_id,vol.name)}>

                                {/* FIX 2: Added flex-nowrap to prevent the call button from dropping to the next line */}
                                <div className="row align-items-center flex-nowrap g-3" style={{ height: '100%' }}>
                                    <div className="col-auto">
                                        <div className="card-avatar-wrapper">
                                            {vol.photo_path ? (
                                                <img src={vol.photo_path} alt="user" className="card-avatar-img" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
                                            ) : null}
                                            <div className="card-avatar-placeholder" style={{ display: vol.photo_path ? 'none' : 'flex', backgroundColor: avatarBg, color: avatarText }}>
                                                {vol.name ? vol.name.charAt(0).toUpperCase() : "U"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* FIX 3: Added minWidth: 0 to allow text-truncate to work correctly inside the flex container */}
                                    <div className="col" style={{ minWidth: 0 }}>
                                        <div className="card-middle">
                                            <h3 className="card-title-name text-truncate" title={vol.name}>
                                                {index + 1}. {vol.name || "Unknown"}
                                            </h3>
                                            <div className="card-meta-row d-flex align-items-center flex-wrap gap-2">
                                                <span className={`role-badge ${badgeClass}`}>{userTypeLabel}</span>
                                                <span className="divider-dot">•</span>
                                                <span className="member-count-label">{txt.members} <strong>{memberCount}</strong></span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-auto">
                                        <button className="btn-call-circle" onClick={(e) => { e.stopPropagation(); window.open(`tel:${vol.mobile_no}`); }} disabled={!vol.mobile_no} title={txt.call}>
                                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <style jsx>{`
                /* Subtitle Styles - Option 7: Gradient Line */
                .subtitle-container {
                    margin-top: 8px;
                    margin-bottom: 20px;
                    position: relative;
                }
                .subtitle-content {
                    font-size: 15px;
                    color: #334155;
                    margin: 0 0 10px 0;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .stats-item {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .stats-divider {
                    color: #cbd5e1;
                    font-size: 16px;
                }
                .count-badge-stats {
                    background: #e2e8f0;
                    color: #475569;
                    padding: 2px 10px;
                    border-radius: 20px;
                    font-weight: 700;
                    font-size: 13px;
                    margin-left: 4px;
                }
                .count-badge-stats.green {
                    background: #dcfce7;
                    color: #166534;
                }
                .subtitle-container::after {
                    content: "";
                    display: block;
                    width: 60px;
                    height: 3px;
                    background: linear-gradient(to right, #2563eb, #60a5fa);
                    border-radius: 3px;
                    margin-top: 4px;
                }

                .count-badge { background: #e2e8f0; color: #475569; padding: 2px 8px; border-radius: 12px; font-weight: 700; font-size: 0.85rem; }
                .search-wrapper { flex-grow: 1; position: relative; max-width:450px } 
                .search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); width: 18px; height: 18px; color: #94a3b8; }
                .search-input { width: 100%; padding: 12px 12px 12px 40px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; outline: none; }
                .volunteer-card-web { background: white; border-radius: 12px; padding: 16px; border: 1px solid #e5e7eb; transition: transform 0.2s, box-shadow 0.2s; cursor: pointer; }
                .volunteer-card-web:hover { transform: translateY(-3px); box-shadow: 0 8px 15px -3px rgba(0,0,0,0.1); border-color: #cbd5e1; }
                .card-avatar-wrapper { width: 56px; height: 56px; }
                .card-avatar-img, .card-avatar-placeholder { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
                .card-avatar-placeholder { display: flex; align-items: center; justify-content: center; font-size: 1.5rem; font-weight: 700; }
                .card-title-name { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin: 0 0 4px; }
                .role-badge { font-size: 0.7rem; font-weight: 700; padding: 3px 8px; border-radius: 4px; color: white; text-transform: uppercase; }
                .badge-red { background-color: #ef4444; }
                .badge-orange { background-color: #f59e0b; }
                .badge-blue { background-color: #3b82f6; }
                .member-count-label { font-size: 0.85rem; color: #4b5563; font-weight: 500; }
                .btn-call-circle { width: 42px; height: 42px; border-radius: 50%; background-color: #e0f2fe; color: #0284c7; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; }
                .btn-call-circle:hover { background-color: #0ea5e9; color: white; transform: scale(1.1); }
                .search-row-flex { display: flex; gap: 12px; align-items: center; justify-content: space-between; width: 100%; }
                .btn-booth-wise { background-color: #3b82f6; color: white; padding: 0 20px; height: 48px; border-radius: 8px; border: none; font-weight: 600; cursor: pointer; display: flex; align-items: center; white-space: nowrap; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                
                /* --- COUNT DISPLAY --- */
                .count-display-modern {
                    display: inline-flex;
                    align-items: center;
                    background: #eff6ff;
                    padding: 8px 16px;
                    border-radius: 12px;
                    border: 1px solid #bfdbfe;
                    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.05);
                    gap: 8px;
                }
                .count-label {
                    color: #1e40af;
                    font-weight: 600;
                    font-size: 15px;
                }
                .count-number {
                    background: transparent;
                    color: #1d4ed8;
                    font-weight: 800;
                    font-size: 16px;
                }
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                .empty-state { background: #f8fafc; border-radius: 20px; border: 2px dashed #e2e8f0; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media (max-width: 640px) {
                    .subtitle-content {
                        font-size: 13px;
                        gap: 8px;
                        flex-wrap: wrap;
                    }
                    .subtitle-container::after {
                        width: 50px;
                        height: 2px;
                    }
                    .stats-divider {
                        display: none;
                    }
                    .stats-item {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default VolunteersPhonebook;